import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Buildings,
  ChartBar,
  CurrencyInr,
  DownloadSimple,
  FileText,
  GraduationCap,
  PencilSimple,
  Tray,
  Trash,
  Users,
  UserPlus,
} from "@phosphor-icons/react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DashboardShell from "@/components/DashboardShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { daysAgoLocal, formatErr, inr, todayLocal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { id: "overview", label: "Overview", icon: ChartBar },
  { id: "schools", label: "Organizations & Branches", icon: Buildings },
  { id: "leads", label: "Website Leads", icon: Tray },
  { id: "reports", label: "School Reports", icon: FileText },
  { id: "migration", label: "Excel Migration", icon: DownloadSimple },
];

const ORGANIZATION_TYPES = [
  { value: "school", label: "School / Preschool" },
  { value: "phonetics", label: "Phonetics Centre" },
  { value: "tutorial", label: "Tutorial / Coaching" },
  { value: "college", label: "College / Pharmacy College" },
  { value: "other", label: "Other Education Organisation" },
];

const organizationTypeLabel = (value) =>
  ORGANIZATION_TYPES.find((item) => item.value === value)?.label || "School / Preschool";

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] transition-all duration-300" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`w-10 h-10 rounded-xl grid place-items-center ${tint}`}>
      <Icon size={20} weight="duotone" />
    </div>
    <p className="font-heading text-3xl font-extrabold mt-3">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export default function SuperAdmin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchBranch } = useAuth();
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [schools, setSchools] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [schoolForm, setSchoolForm] = useState({ name: "", organization_type: "school", email: "", phone: "", address: "", plan: "starter", admin_name: "", admin_email: "", admin_phone: "", admin_password: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmIds, setConfirmIds] = useState(null);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [migrationSchool, setMigrationSchool] = useState("");
  const [migrationYears, setMigrationYears] = useState([]);
  const [migrationYear, setMigrationYear] = useState("");
  const [migrationFile, setMigrationFile] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationBusy, setMigrationBusy] = useState(false);
  const [branchOrganization, setBranchOrganization] = useState(null);

  const [branchForm, setBranchForm] = useState({
  branch_name: "",
  branch_code: "",
  email: "",
  phone: "",
  address: "",
  plan: "starter",
  admin_name: "",
  admin_email: "",
  admin_phone: "",
  admin_password: "",
});

const [branchSaving, setBranchSaving] = useState(false);
  const [openingSchoolId, setOpeningSchoolId] = useState("");

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      name: s.name,
      email: s.email || "",
      phone: s.phone || "",
      address: s.address || "",
      plan: s.plan,
      status: s.status,
      notifications_opt_in: !!s.notifications_opt_in,
      organization_type: s.organization_type || "school",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setActionBusy(true);
    try {
      await api.patch(`/admin/schools/${editing.id}`, editForm);
      toast.success("Organisation updated");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmDelete = async () => {
    setActionBusy(true);
    try {
      await api.delete(`/admin/schools/${deleting.id}`);
      toast.success(`${deleting.name} removed permanently`);
      setDeleting(null);
      loadAll();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setActionBusy(false);
    }
  };

  const loadAll = async () => {
    try {
      const [m, s, l] = await Promise.all([api.get("/admin/metrics"), api.get("/admin/schools"), api.get("/admin/leads")]);
      setMetrics(m.data);
      setSchools(s.data);
      setLeads(l.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const createSchool = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/schools", schoolForm);
      toast.success(`Organisation created. Admin login: ${schoolForm.admin_email}`);
      setSchoolForm({ name: "", organization_type: "school", email: "", phone: "", address: "", plan: "starter", admin_name: "", admin_email: "", admin_phone: "", admin_password: "" });
      loadAll();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };
  const openBranchDialog = (school) => {
    setBranchOrganization(school);
    setBranchForm({
      branch_name: "",
      branch_code: "",
      email: "",
      phone: "",
      address: "",
      plan: school?.plan || "starter",
      admin_name: "",
      admin_email: "",
      admin_phone: "",
      admin_password: "",
    });
    setBranchDialogOpen(true);
  };

  const createBranch = async (e) => {
  e.preventDefault();

  if (!branchOrganization?.organization_id) {
    return toast.error(t("organization.organizationNotSelected"));
  }

  setBranchSaving(true);

  try {
    await api.post(
      `/admin/organizations/${branchOrganization.organization_id}/branches`,
      {
        ...branchForm,
        branch_name: branchForm.branch_name.trim(),
        branch_code: branchForm.branch_code.trim().toUpperCase(),
      }
    );

    toast.success(t("organization.branchCreated", { name: branchForm.branch_name }));

    setBranchForm({
      branch_name: "",
      branch_code: "",
      email: "",
      phone: "",
      address: "",
      plan: "starter",
      admin_name: "",
      admin_email: "",
      admin_phone: "",
      admin_password: "",
    });

    setBranchDialogOpen(false);
    setBranchOrganization(null);

    await loadAll();
  } catch (err) {
    toast.error(formatErr(err));
  } finally {
    setBranchSaving(false);
  }
};

  const openSchoolDashboard = async (school) => {
    if (!school?.id) return;
    try {
      setOpeningSchoolId(school.id);
      await switchBranch(school.id);
      navigate("/school");
    } catch (err) {
      toast.error(formatErr(err, t("organization.openBranchFailed", { defaultValue: "Unable to open branch dashboard" })));
    } finally {
      setOpeningSchoolId("");
    }
  };
  const updateLead = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const filteredLeads = leadFilter === "all" ? leads : leads.filter((l) => l.status === leadFilter);
  const allSelected = filteredLeads.length > 0 && filteredLeads.every((l) => selected.includes(l.id));
  const toggleAll = () => setSelected(allSelected ? [] : filteredLeads.map((l) => l.id));
  const toggleOne = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const confirmDeleteLeads = async () => {
    if (!confirmIds?.length) return;
    setActionBusy(true);
    try {
      if (confirmIds.length === 1) {
        await api.delete(`/admin/leads/${confirmIds[0]}`);
      } else {
        await api.post("/admin/leads/bulk-delete", { ids: confirmIds });
      }
      toast.success(`${confirmIds.length} lead${confirmIds.length === 1 ? "" : "s"} deleted`);
      setSelected([]);
      setConfirmIds(null);
      loadAll();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setActionBusy(false);
    }
  };

  const loadMigrationYears = async (schoolId) => {
    setMigrationSchool(schoolId); setMigrationYear(""); setMigrationResult(null);
    if (!schoolId) return setMigrationYears([]);
    try { const r = await api.get("/admin/migration/academic-years", { params: { school_id: schoolId } }); setMigrationYears(r.data || []); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const downloadMigrationTemplate = async () => {
    try { const r = await api.get("/admin/migration/students/template", { responseType: "blob" }); const u=URL.createObjectURL(r.data); const a=document.createElement("a"); a.href=u; a.download="edujac_student_migration_template.xlsx"; a.click(); URL.revokeObjectURL(u); } catch(e) { toast.error(formatErr(e)); }
  };
  const runMigration = async (commit=false) => {
    if (!migrationSchool || !migrationYear || !migrationFile) return toast.error("Select branch, academic year and Excel file");
    const fd=new FormData(); fd.append("school_id", migrationSchool); fd.append("academic_year_id", migrationYear); fd.append("file", migrationFile);
    setMigrationBusy(true);
    try { const r=await api.post(commit ? "/admin/migration/students/import" : "/admin/migration/students/dry-run", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMigrationResult(r.data); toast.success(commit ? `Imported ${r.data.imported} students` : "Dry run completed"); if (commit) loadAll(); }
    catch(e) { toast.error(formatErr(e)); } finally { setMigrationBusy(false); }
  };

  const organizationCount = new Set(
    schools.map((school) => school.organization_id).filter(Boolean)
  ).size;
  const branchCount = schools.length;

  return (
    <DashboardShell brand="Platform Owner" nav={NAV} active={tab} onNavigate={setTab}>
      <div data-testid="admin-dashboard" className="contents">
      {tab === "overview" && metrics && (
        <div data-testid="admin-overview">
          <h1 className="font-heading text-2xl font-extrabold mb-6">Platform overview</h1>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={Buildings} label={t("organization.organizations", { defaultValue: "Organizations" })} value={organizationCount} tint="bg-brand-coral/10 text-brand-coral" />
            <StatCard icon={Buildings} label={t("organization.branches", { defaultValue: "Branches" })} value={branchCount} tint="bg-amber-100 text-amber-700" />
            <StatCard icon={GraduationCap} label="Students" value={metrics.students} tint="bg-brand-mint/15 text-teal-600" />
            <StatCard icon={Users} label="Teachers" value={metrics.teachers} tint="bg-status-blue/10 text-status-blue" />
            <StatCard icon={CurrencyInr} label="Fees collected" value={inr(metrics.fees_collected)} tint="bg-status-green/10 text-status-green" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h2 className="font-heading font-bold mb-4">Students per branch</h2>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={schools.map((s) => ({ name: s.name.split(" ")[0], students: s.student_count }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="students" fill="#FF6B6B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h2 className="font-heading font-bold mb-2">Lead pipeline</h2>
              <p className="text-sm text-slate-500 mb-4">{metrics.leads_new} new of {metrics.leads_total} total enquiries</p>
              <div className="flex gap-3">
                {["new", "contacted", "converted"].map((s) => (
                  <div key={s} className="flex-1 bg-[#F4F5F7] rounded-xl p-4 text-center">
                    <p className="font-heading text-2xl font-extrabold">{leads.filter((l) => l.status === s).length}</p>
                    <p className="text-xs text-slate-500 capitalize mt-1">{s}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}


      {tab === "migration" && (
        <div data-testid="admin-excel-migration" className="max-w-5xl">
          <h1 className="font-heading text-2xl font-extrabold">Excel Migration</h1>
          <p className="text-sm text-slate-500 mt-1 mb-5">Safe student master migration: template → branch/year → dry run → fix errors → explicit import. Existing students are never overwritten.</p>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
            <div className="flex flex-wrap gap-2"><Button variant="outline" onClick={downloadMigrationTemplate}><DownloadSimple size={16} className="mr-1"/>Download template</Button></div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Branch</Label><Select value={migrationSchool} onValueChange={loadMigrationYears}><SelectTrigger className="mt-1"><SelectValue placeholder="Select branch"/></SelectTrigger><SelectContent>{schools.map((x)=><SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Academic Year</Label><Select value={migrationYear} onValueChange={setMigrationYear}><SelectTrigger className="mt-1"><SelectValue placeholder="Select academic year"/></SelectTrigger><SelectContent>{migrationYears.map((x)=><SelectItem key={x.id} value={x.id}>{x.name}{x.is_current ? " (Current)" : ""}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Completed EDUJAC Excel template</Label><Input className="mt-1" type="file" accept=".xlsx" onChange={(e)=>{setMigrationFile(e.target.files?.[0] || null); setMigrationResult(null);}} /></div>
            <div className="flex flex-wrap gap-2"><Button disabled={migrationBusy} onClick={()=>runMigration(false)}>Dry Run / Validate</Button><Button variant="outline" disabled={migrationBusy || !migrationResult?.ok || migrationResult?.error_rows>0} onClick={()=>runMigration(true)}>Import Validated File</Button></div>
            {migrationResult && <div className={`rounded-xl p-4 text-sm ${migrationResult.error_rows ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-800"}`}><p className="font-bold">Valid rows: {migrationResult.valid_rows ?? migrationResult.imported ?? 0} · Error rows: {migrationResult.error_rows ?? 0}</p>{(migrationResult.errors || []).slice(0,20).map((e)=><p key={e.row}>Row {e.row}: {(e.errors || []).join(", ")}</p>)}</div>}
          </div>
        </div>
      )}

      {tab === "schools" && (
        <div data-testid="admin-schools">
          <h1 className="font-heading text-2xl font-extrabold mb-6">{t("organization.organizations", { defaultValue: "Organizations" })} &amp; {t("organization.branches", { defaultValue: "Branches" })}</h1>
          <form onSubmit={createSchool} className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="create-school-form">
            <h2 className="font-heading font-bold flex items-center gap-2 mb-4">
              <UserPlus size={18} weight="duotone" className="text-brand-coral" /> Onboard a new organization
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Organization name</Label>
                <Input data-testid="school-name-input" required className="mt-1.5" value={schoolForm.name} onChange={(e) => setSchoolForm({ ...schoolForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Organisation type</Label>
                <Select value={schoolForm.organization_type} onValueChange={(v) => setSchoolForm({ ...schoolForm, organization_type: v })}>
                  <SelectTrigger data-testid="organization-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORGANIZATION_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organisation email</Label>
                <Input data-testid="school-email-input" type="email" className="mt-1.5" value={schoolForm.email} onChange={(e) => setSchoolForm({ ...schoolForm, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input className="mt-1.5" value={schoolForm.phone} onChange={(e) => setSchoolForm({ ...schoolForm, phone: e.target.value })} />
              </div>
              <div>
                <Label>Plan</Label>
                <Select value={schoolForm.plan} onValueChange={(v) => setSchoolForm({ ...schoolForm, plan: v })}>
                  <SelectTrigger data-testid="school-plan-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Sprout</SelectItem>
                    <SelectItem value="growth">Bloom</SelectItem>
                    <SelectItem value="enterprise">Evergreen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Address</Label>
                <Input className="mt-1.5" value={schoolForm.address} onChange={(e) => setSchoolForm({ ...schoolForm, address: e.target.value })} />
              </div>
              <div>
                <Label>Organization admin name</Label>
                <Input data-testid="school-admin-name" required className="mt-1.5" value={schoolForm.admin_name} onChange={(e) => setSchoolForm({ ...schoolForm, admin_name: e.target.value })} />
              </div>
              <div>
                <Label>Organization admin email</Label>
                <Input data-testid="school-admin-email" type="email" required className="mt-1.5" value={schoolForm.admin_email} onChange={(e) => setSchoolForm({ ...schoolForm, admin_email: e.target.value })} />
              </div>
              <div>
                <Label>Organization admin mobile</Label>
                <Input data-testid="school-admin-phone" type="tel" className="mt-1.5" value={schoolForm.admin_phone} onChange={(e) => setSchoolForm({ ...schoolForm, admin_phone: e.target.value })} placeholder="+91 …" />
              </div>
              <div>
                <Label>Organization admin password</Label>
                <Input data-testid="school-admin-password" required className="mt-1.5" value={schoolForm.admin_password} onChange={(e) => setSchoolForm({ ...schoolForm, admin_password: e.target.value })} />
              </div>
              <div className="flex items-end">
                <Button type="submit" data-testid="create-school-btn" disabled={saving} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                  {saving ? "Creating…" : "Create organization"}
                </Button>
              </div>
            </div>
          </form>
          <div className="lg:hidden space-y-3">
            {schools.map((s) => (
              <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`school-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-sm">{s.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{organizationTypeLabel(s.organization_type)}</p>
                    <div className="mt-1">
                      <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ${s.is_main_branch ? "bg-status-blue/10 text-status-blue" : "bg-slate-100 text-slate-600"}`}>
                        {s.is_main_branch
                          ? t("organization.mainBranch", { defaultValue: "Main Branch" })
                          : `${t("organization.branch", { defaultValue: "Branch" })}${s.branch_code ? ` · ${s.branch_code}` : ""}`}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{s.email || "No school email"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{s.phone || "—"} · <span className="capitalize">{s.plan}</span></p>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${s.status === "active" ? "text-status-green bg-status-green/10" : "text-status-red bg-status-red/10"}`}>{s.status}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-600">
                  <span><span className="font-bold">{s.teacher_count}</span> teachers</span>
                  <span><span className="font-bold">{s.student_count}</span> students</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.notifications_opt_in ? "text-status-blue bg-status-blue/10" : "text-slate-400 bg-slate-100"}`} data-testid={`optin-badge-m-${s.id}`}>
                    {s.notifications_opt_in ? "Opted in" : "Opted out"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`open-school-m-${s.id}`}
                    disabled={openingSchoolId === s.id || s.status === "inactive"}
                    onClick={() => openSchoolDashboard(s)}
                    className="flex-1 rounded-full font-bold border-slate-300 text-status-blue hover:text-status-blue"
                  >
                    <ChartBar size={14} className="mr-1.5" />
                    {openingSchoolId === s.id
                      ? t("common.loading", { defaultValue: "Loading..." })
                      : `${t("common.view", { defaultValue: "View" })} ${t("navigation.dashboard", { defaultValue: "Dashboard" })}`}
                  </Button>
                  {s.is_main_branch && s.organization_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`add-branch-m-${s.id}`}
                      onClick={() => openBranchDialog(s)}
                      className="flex-1 rounded-full font-bold border-slate-300 text-status-green hover:text-status-green"
                    >
                      <Buildings size={14} className="mr-1.5" /> {t("organization.addBranch")}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" data-testid={`edit-school-m-${s.id}`} onClick={() => openEdit(s)} className="flex-1 rounded-full font-bold border-slate-300">
                    <PencilSimple size={14} className="mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" data-testid={`delete-school-m-${s.id}`} onClick={() => setDeleting(s)} className="flex-1 rounded-full font-bold border-slate-300 text-status-red hover:text-status-red">
                    <Trash size={14} className="mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden lg:block bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3">Organisation / Branch</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Phone</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Teachers</th>
                  <th className="px-5 py-3">Students</th>
                  <th className="px-5 py-3">Alerts</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-brand-coral/5 transition-colors duration-200" data-testid={`school-row-${s.id}`}>
                    <td className="px-5 py-3.5 font-semibold whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openSchoolDashboard(s)}
                        disabled={openingSchoolId === s.id || s.status === "inactive"}
                        className="text-left group disabled:opacity-50"
                        title={`${t("common.view", { defaultValue: "View" })} ${t("navigation.dashboard", { defaultValue: "Dashboard" })}`}
                      >
                        <div className="text-slate-900 group-hover:text-status-blue transition-colors duration-200">
                          {s.name}
                        </div>
                        <span className={`inline-flex mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.is_main_branch ? "bg-status-blue/10 text-status-blue" : "bg-slate-100 text-slate-600"}`}>
                          {s.is_main_branch
                            ? t("organization.mainBranch", { defaultValue: "Main Branch" })
                            : `${t("organization.branch", { defaultValue: "Branch" })}${s.branch_code ? ` · ${s.branch_code}` : ""}`}
                        </span>
                        <div className="mt-1 text-[11px] font-semibold text-status-blue">
                          {openingSchoolId === s.id
                            ? t("common.loading", { defaultValue: "Loading..." })
                            : `${t("common.view", { defaultValue: "View" })} ${t("navigation.dashboard", { defaultValue: "Dashboard" })}`}
                        </div>
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{s.email || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{s.phone || "—"}</td>
                    <td className="px-5 py-3.5 capitalize">{s.plan}</td>
                    <td className="px-5 py-3.5">{s.teacher_count}</td>
                    <td className="px-5 py-3.5">{s.student_count}</td>
                      <td className="px-5 py-3.5">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.notifications_opt_in ? "text-status-blue bg-status-blue/10" : "text-slate-400 bg-slate-100"}`} data-testid={`optin-badge-${s.id}`}>
                        {s.notifications_opt_in ? "Opted in" : "Opted out"}
                      </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-600">{organizationTypeLabel(s.organization_type)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.status === "active" ? "text-status-green bg-status-green/10" : "text-status-red bg-status-red/10"}`}>{s.status}</span>
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button
                        data-testid={`open-school-${s.id}`}
                        onClick={() => openSchoolDashboard(s)}
                        disabled={openingSchoolId === s.id || s.status === "inactive"}
                        className="p-2 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200 disabled:opacity-40"
                        title={`${t("common.view", { defaultValue: "View" })} ${t("navigation.dashboard", { defaultValue: "Dashboard" })}`}
                      >
                        <ChartBar size={16} />
                      </button>
                      {s.is_main_branch && s.organization_id && (
                        <button
                          data-testid={`add-branch-${s.id}`}
                          onClick={() => openBranchDialog(s)}
                          className="p-2 rounded-lg text-slate-400 hover:text-status-green hover:bg-status-green/10 transition-colors duration-200"
                          title={t("organization.addBranch")}
                        >
                          <Buildings size={16} />
                        </button>
                      )}
                      <button
                        data-testid={`edit-school-${s.id}`}
                        onClick={() => openEdit(s)}
                        className="p-2 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200"
                        title="Edit school"
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        data-testid={`delete-school-${s.id}`}
                        onClick={() => setDeleting(s)}
                        className="p-2 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200"
                        title="Delete school"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Dialog
            open={branchDialogOpen}
            onOpenChange={(open) => {
              setBranchDialogOpen(open);
              if (!open) setBranchOrganization(null);
            }}
          >
            <DialogContent className="rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {t("organization.addBranch")}
                  {branchOrganization?.name ? ` · ${branchOrganization.name}` : ""}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={createBranch} className="space-y-4" data-testid="create-branch-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t("organization.branchName")}</Label>
                    <Input
                      data-testid="branch-name-input"
                      required
                      className="mt-1.5"
                      value={branchForm.branch_name}
                      onChange={(e) => setBranchForm({ ...branchForm, branch_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("organization.branchCode")}</Label>
                    <Input
                      data-testid="branch-code-input"
                      required
                      className="mt-1.5 uppercase"
                      value={branchForm.branch_code}
                      onChange={(e) => setBranchForm({ ...branchForm, branch_code: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div>
                    <Label>{t("common.email")}</Label>
                    <Input
                      type="email"
                      className="mt-1.5"
                      value={branchForm.email}
                      onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("common.phone")}</Label>
                    <Input
                      className="mt-1.5"
                      value={branchForm.phone}
                      onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>{t("common.address", "Address")}</Label>
                  <Input
                    className="mt-1.5"
                    value={branchForm.address}
                    onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                  />
                </div>

                <div>
                  <Label>{t("common.plan", "Plan")}</Label>
                  <Select value={branchForm.plan} onValueChange={(v) => setBranchForm({ ...branchForm, plan: v })}>
                    <SelectTrigger data-testid="branch-plan-select" className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Sprout</SelectItem>
                      <SelectItem value="growth">Bloom</SelectItem>
                      <SelectItem value="enterprise">Evergreen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm font-bold text-slate-700 mb-3">{t("organization.branchAdmin")}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>{t("organization.branchAdminName")}</Label>
                      <Input
                        data-testid="branch-admin-name"
                        required
                        className="mt-1.5"
                        value={branchForm.admin_name}
                        onChange={(e) => setBranchForm({ ...branchForm, admin_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t("organization.branchAdminEmail")}</Label>
                      <Input
                        data-testid="branch-admin-email"
                        type="email"
                        required
                        className="mt-1.5"
                        value={branchForm.admin_email}
                        onChange={(e) => setBranchForm({ ...branchForm, admin_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Branch admin mobile</Label>
                      <Input data-testid="branch-admin-phone" type="tel" className="mt-1.5" value={branchForm.admin_phone} onChange={(e) => setBranchForm({ ...branchForm, admin_phone: e.target.value })} placeholder="+91 …" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>{t("organization.branchAdminPassword")}</Label>
                      <Input
                        data-testid="branch-admin-password"
                        type="password"
                        required
                        className="mt-1.5"
                        value={branchForm.admin_password}
                        onChange={(e) => setBranchForm({ ...branchForm, admin_password: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="create-branch-btn"
                  disabled={branchSaving}
                  className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold"
                >
                  {branchSaving ? t("organization.creatingBranch") : t("organization.createBranch")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle className="font-heading">Edit organisation</DialogTitle></DialogHeader>
              {editForm && (
                <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-school-form">
                  <div><Label htmlFor="edit-school-name">School name</Label><Input id="edit-school-name" data-testid="edit-school-name" required className="mt-1.5" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label htmlFor="edit-school-email">School email</Label><Input id="edit-school-email" type="email" className="mt-1.5" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
                    <div><Label htmlFor="edit-school-phone">Phone</Label><Input id="edit-school-phone" className="mt-1.5" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                  </div>
                  {editing?.is_main_branch && (
                    <div>
                      <Label>Organisation type</Label>
                      <Select value={editForm.organization_type || "school"} onValueChange={(v) => setEditForm({ ...editForm, organization_type: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>{ORGANIZATION_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label htmlFor="edit-school-address">Address</Label><Input id="edit-school-address" className="mt-1.5" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Plan</Label>
                      <Select value={editForm.plan} onValueChange={(v) => setEditForm({ ...editForm, plan: v })}>
                        <SelectTrigger data-testid="edit-school-plan" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="starter">Sprout</SelectItem>
                          <SelectItem value="growth">Bloom</SelectItem>
                          <SelectItem value="enterprise">Evergreen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                        <SelectTrigger data-testid="edit-school-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive (deactivated)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-[#F4F5F7] rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">External notifications opt-in</p>
                      <p className="text-xs text-slate-500">Allow this school to send Email & WhatsApp broadcasts</p>
                    </div>
                    <Switch
                      data-testid="edit-school-optin-switch"
                      checked={editForm.notifications_opt_in}
                      onCheckedChange={(c) => setEditForm({ ...editForm, notifications_opt_in: c })}
                    />
                  </div>
                  <Button type="submit" data-testid="edit-school-save-btn" disabled={actionBusy} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">
                    {actionBusy ? "Saving…" : "Save changes"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>

          <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Delete {deleting?.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the school and all its data — teachers, students, attendance, homework, fees and broadcasts. To keep the data, edit the school and set its status to Inactive instead.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-school-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-school-confirm-btn"
                  onClick={confirmDelete}
                  disabled={actionBusy}
                  className="bg-status-red hover:bg-status-red/90 text-white"
                >
                  {actionBusy ? "Deleting…" : "Delete permanently"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {tab === "leads" && (
        <div data-testid="admin-leads">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h1 className="font-heading text-2xl font-extrabold">Website leads</h1>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex lg:hidden items-center gap-2">
                <Checkbox data-testid="select-all-leads-m" checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all leads" />
                <span className="text-xs font-semibold text-slate-500">Select all</span>
              </div>
              <Button
                variant="outline"
                data-testid="delete-selected-leads-btn"
                disabled={selected.length === 0}
                onClick={() => setConfirmIds(selected)}
                className="rounded-full font-bold border-status-red text-status-red hover:bg-status-red/10 disabled:opacity-50"
              >
                <Trash size={15} className="mr-1.5" /> Delete Selected{selected.length > 0 ? ` (${selected.length})` : ""}
              </Button>
              <Select value={leadFilter} onValueChange={(v) => { setLeadFilter(v); setSelected([]); }}>
                <SelectTrigger data-testid="lead-filter" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="lg:hidden space-y-3">
            {filteredLeads.map((l) => (
              <div key={l.id} className={`bg-white border rounded-2xl p-4 ${selected.includes(l.id) ? "border-status-red/40 bg-status-red/5" : "border-slate-200"}`} data-testid={`lead-card-${l.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <Checkbox data-testid={`select-lead-m-${l.id}`} checked={selected.includes(l.id)} onCheckedChange={() => toggleOne(l.id)} aria-label={`Select ${l.school_name}`} />
                  <button data-testid={`delete-lead-m-${l.id}`} onClick={() => setConfirmIds([l.id])} className="p-1.5 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete lead">
                    <Trash size={15} />
                  </button>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-heading font-bold text-sm">{l.school_name}</p>
                  <span className="text-xs text-slate-500 capitalize whitespace-nowrap">{l.school_type} · {l.student_count} students</span>
                </div>
                {l.message && <p className="text-xs text-slate-500 mt-1">{l.message}</p>}
                <p className="text-sm font-semibold mt-2">{l.owner_name}</p>
                <p className="text-xs text-slate-500 break-all">{l.email} · {l.phone}</p>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <Select value={l.status} onValueChange={(v) => updateLead(l.id, v)}>
                    <SelectTrigger data-testid={`lead-status-m-${l.id}`} className="w-full h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden lg:block bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 w-10">
                    <Checkbox data-testid="select-all-leads" checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all leads" />
                  </th>
                  <th className="px-5 py-3">School</th>
                  <th className="px-5 py-3">Contact</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Students</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => (
                  <tr key={l.id} className={`border-t border-slate-100 ${selected.includes(l.id) ? "bg-status-red/5" : ""}`} data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-3.5">
                      <Checkbox data-testid={`select-lead-${l.id}`} checked={selected.includes(l.id)} onCheckedChange={() => toggleOne(l.id)} aria-label={`Select ${l.school_name}`} />
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold">{l.school_name}</p>
                      {l.message && <p className="text-xs text-slate-500 mt-0.5 max-w-xs truncate">{l.message}</p>}
                    </td>
                    <td className="px-5 py-3.5">
                      <p>{l.owner_name}</p>
                      <p className="text-xs text-slate-500">{l.email} · {l.phone}</p>
                    </td>
                    <td className="px-5 py-3.5 capitalize">{l.school_type}</td>
                    <td className="px-5 py-3.5">{l.student_count}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        <Select value={l.status} onValueChange={(v) => updateLead(l.id, v)}>
                          <SelectTrigger data-testid={`lead-status-${l.id}`} className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                        <button data-testid={`delete-lead-${l.id}`} onClick={() => setConfirmIds([l.id])} className="p-2 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete lead">
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <AlertDialog open={!!confirmIds} onOpenChange={(o) => !o && setConfirmIds(null)}>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Delete {confirmIds?.length === 1 ? "this lead" : `${confirmIds?.length || 0} leads`}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to permanently delete the selected lead(s)? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-leads-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-leads-confirm-btn"
                  onClick={confirmDeleteLeads}
                  disabled={actionBusy}
                  className="bg-status-red hover:bg-status-red/90 text-white"
                >
                  {actionBusy ? "Deleting…" : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
      {tab === "reports" && <ReportsTab schools={schools} />}
      </div>
    </DashboardShell>
  );
}

function ReportsTab({ schools }) {
  const [schoolId, setSchoolId] = useState("");
  const [period, setPeriod] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [dataset, setDataset] = useState("students");
  const [extract, setExtract] = useState({ from: daysAgoLocal(30), to: todayLocal() });
  const [extractBusy, setExtractBusy] = useState(null);

  useEffect(() => {
    if (!schoolId && schools.length) setSchoolId(schools[0].id);
  }, [schools, schoolId]);

  const downloadDataset = async (fmt) => {
    if (!schoolId) return;
    setExtractBusy(fmt);
    try {
      const res = await api.get("/admin/reports/export", {
        params: { school_id: schoolId, dataset, from: extract.from, to: extract.to, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${dataset}_extract.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    } finally {
      setExtractBusy(null);
    }
  };

  const download = async (fmt) => {
    if (!schoolId) return;
    setBusy(fmt);
    try {
      const res = await api.get("/admin/reports/school", {
        params: { school_id: schoolId, period, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `school_report_${period}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="reports-tab">
      <h1 className="font-heading text-2xl font-extrabold">School reports</h1>
      <p className="text-sm text-slate-500 mt-1">
        Daily, weekly or monthly extracts per school — admin activity, teacher activity, student attendance summaries and fee status.
      </p>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
        <div className="flex flex-wrap items-center gap-4">
          <Select value={schoolId} onValueChange={setSchoolId}>
            <SelectTrigger data-testid="report-school-select" className="w-64"><SelectValue placeholder="Select school" /></SelectTrigger>
            <SelectContent>
              {schools.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            {["daily", "weekly", "monthly"].map((p) => (
              <button
                key={p}
                data-testid={`report-period-${p}`}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors duration-200 ${period === p ? "bg-brand-coral text-white" : "bg-slate-100 text-slate-600"}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" data-testid="report-csv-btn" onClick={() => download("csv")} disabled={!!busy} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
              <DownloadSimple size={16} className="mr-1.5" /> {busy === "csv" ? "Preparing…" : "CSV"}
            </Button>
            <Button data-testid="report-excel-btn" onClick={() => download("xlsx")} disabled={!!busy} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
              <DownloadSimple size={16} className="mr-1.5" /> {busy === "xlsx" ? "Preparing…" : "Excel"}
            </Button>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs text-slate-500">
          <div className="bg-[#F4F5F7] rounded-xl p-3"><span className="font-bold text-slate-700 block mb-1">Admin activity</span>Broadcasts sent, payments recorded, collections in period</div>
          <div className="bg-[#F4F5F7] rounded-xl p-3"><span className="font-bold text-slate-700 block mb-1">Teacher activity</span>Attendance marks and homework posts per teacher</div>
          <div className="bg-[#F4F5F7] rounded-xl p-3"><span className="font-bold text-slate-700 block mb-1">Attendance summary</span>Present/absent totals and attendance rate</div>
          <div className="bg-[#F4F5F7] rounded-xl p-3"><span className="font-bold text-slate-700 block mb-1">Fee status</span>Invoiced, collected, outstanding, open invoices</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4" data-testid="dataset-extract-card">
        <h2 className="font-heading font-bold">Custom data extract</h2>
        <p className="text-xs text-slate-500 mb-4 mt-1">Pick a dataset and date range, then download as CSV or Excel.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={dataset} onValueChange={setDataset}>
            <SelectTrigger data-testid="extract-dataset-select" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="students">Students (name, phone, class…)</SelectItem>
              <SelectItem value="attendance">Attendance (date range)</SelectItem>
              <SelectItem value="teachers">Teachers</SelectItem>
              <SelectItem value="fees">Fee records</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" data-testid="extract-from" value={extract.from} onChange={(e) => setExtract({ ...extract, from: e.target.value })} className="w-40" />
          <span className="text-slate-400 text-sm">to</span>
          <Input type="date" data-testid="extract-to" value={extract.to} onChange={(e) => setExtract({ ...extract, to: e.target.value })} className="w-40" />
          <Button variant="outline" data-testid="extract-csv-btn" onClick={() => downloadDataset("csv")} disabled={!!extractBusy} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <DownloadSimple size={15} className="mr-1" /> {extractBusy === "csv" ? "Preparing…" : "CSV"}
          </Button>
          <Button data-testid="extract-excel-btn" onClick={() => downloadDataset("xlsx")} disabled={!!extractBusy} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
            <DownloadSimple size={15} className="mr-1" /> {extractBusy === "xlsx" ? "Preparing…" : "Excel"}
          </Button>
        </div>
        {dataset === "attendance" && <p className="text-xs text-slate-400 mt-3">Attendance extracts use the from/to date range (max 3 months). Other datasets export all records.</p>}
      </div>
    </div>
  );
}
